import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import SummarizationLoaderComponent from 'components/ai/SummarizationLoaderComponent';
import JobbableAttachmentJobSubscriber from 'components/shared/ai/summary/JobbableAttachmentJobSubscriber';
import TimingExplainer from 'components/shared/ai/TimingExplainer';
import { useStageTimer } from 'hooks/useStageTimer';
import { SummarizableReview } from 'lib/aiJobHelpers';

type SummaryLoadingStateProps = {
  review: SummarizableReview;
  onLoadingFinished: () => void;
};

export default function SummaryLoadingState({ review, onLoadingFinished }: SummaryLoadingStateProps) {
  const { attachments, summaryJob } = review;

  const fileCount = attachments.length;
  const summaryJobUpdatedAt = summaryJob?.updatedAt;

  // Only start the timer when the job is awaiting (file processing is done)
  const timerReferenceDate = summaryJob?.isAwaiting ? summaryJobUpdatedAt : null;
  const onFinalStage = useStageTimer(timerReferenceDate, 60);

  const stage = useMemo(() => {
    if (summaryJob?.isCompleted) {
      return 4;
    }
    if (summaryJob?.isAwaiting) {
      if (onFinalStage) {
        return 3;
      } else {
        return 2;
      }
    }
    if (summaryJob?.isPending) {
      return 1;
    }
    return 0;
  }, [summaryJob, onFinalStage]);

  const [currentStage, setCurrentStage] = useState(stage);

  useEffect(() => {
    setCurrentStage(stage);
  }, [stage]);

  const count = useMemo(() => {
    return currentStage
      ? attachments.filter((attachment) => attachment.extractDataAIJob && !attachment.extractDataAIJob.isProcessing) // completed or errored
          .length
      : 0;
  }, [currentStage, attachments]);

  const [completedCount, setCompletedCount] = useState(count);

  useEffect(() => {
    if (currentStage) {
      setTimeout(() => {
        setCompletedCount(count);
      }, 500);
    }
  }, [currentStage, count]);

  const stages = useMemo(
    () => [
      {
        title: 'Processing files',
        documentStages: [
          { title: 'Scanning document text', count: completedCount },
          { title: 'Extracting key information', count: completedCount },
          { title: 'Creating source citations', count: completedCount },
        ],
      },
      { title: 'Generating summary' },
      { title: 'Finishing up' },
    ],
    [completedCount]
  );

  return (
    <LoaderContainer>
      {attachments.map((attachment) => (
        <JobbableAttachmentJobSubscriber key={attachment.id} attachment={attachment} jobbable={review} />
      ))}
      <SummarizationLoaderComponent
        type='summary'
        fileCount={fileCount}
        stages={stages}
        stage={currentStage}
        onLoadingFinished={onLoadingFinished}
      />
      <TimingExplainer jobType='summary' />
    </LoaderContainer>
  );
}

const LoaderContainer = styled.div`
  margin: 56px 0 112px 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;
